import { Component, computed, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import {
  CalendarOptions,
  DatesSetArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';

import { firstValueFrom } from 'rxjs';
import {
  AppointmentForCalendarResponse,
  AppointmentsClient,
  AppointmentStatus,
  WorkingTimeClient,
} from '../../../infrastructure/shared/model/fss.api';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { Arrow } from '../../../infrastructure/shared/icons/arrow/arrow';
import { Clock } from '../../../infrastructure/shared/icons/clock/clock';
import { AlignTop02 } from '../../../infrastructure/shared/icons/align-top-02/align-top-02';
import { Rows01 } from '../../../infrastructure/shared/icons/rows-01/rows-01';
import { AppointmentDetailsContent } from '../appointment-details-content/appointment-details-content';
import { AppointmentDetailsDrawer } from '../admin-appointments-table/appointment-details-drawer/appointment-details-drawer';
import {
  eventRanges,
  fitSlotTimes,
  MinuteRange,
  slotTimes,
  wholeDays,
  workingHoursRanges,
} from '../../../infrastructure/shared/services/calendar-hours';
import {
  convertDateToHhMm,
  getDateFormatedSr,
  getDayMonthSr,
  getMonthShortSr,
  getMonthYearSr,
} from '../../../infrastructure/shared/services/date.service';

type CalendarView = 'timeGridWeek' | 'timeGridDay';

interface EventPalette {
  bg: string;
  border: string;
  text: string;
}

const PALETTE: Record<'upcoming' | 'past' | 'canceled' | 'noShow', EventPalette> = {
  upcoming: { bg: '#EFF4FF', border: '#B2CCFF', text: '#1646A4' },
  past: { bg: '#F0F2F5', border: '#E1E2E5', text: '#414651' },
  canceled: { bg: '#FEF3F2', border: '#FDA29B', text: '#912018' },
  noShow: { bg: '#FFFAEB', border: '#FEC84B', text: '#93370D' },
};

@Component({
  standalone: true,
  selector: 'app-admin-appointments-calendar',
  imports: [
    FullCalendarModule,
    FormsModule,
    Select,
    DatePicker,
    ButtonComponent,
    Arrow,
    Clock,
    AlignTop02,
    Rows01,
    AppointmentDetailsContent,
    AppointmentDetailsDrawer,
  ],
  templateUrl: './admin-appointments-calendar.html',
  styleUrl: './admin-appointments-calendar.scss',
})
export class AdminAppointmentsCalendar {
  private static readonly MOBILE_BREAKPOINT = 768;

  private _client = inject(AppointmentsClient);
  private _workingTime = inject(WorkingTimeClient);
  private _host = inject<ElementRef<HTMLElement>>(ElementRef);

  fc = viewChild<FullCalendarComponent>('fc');

  view = signal<CalendarView>('timeGridWeek');
  currentStart = signal<Date>(new Date());
  currentEnd = signal<Date>(new Date());
  miniDate = signal<Date>(new Date());

  selectedAppointmentId = signal<string | undefined>(undefined);

  /** Drawer (week view only) */
  showDrawer = signal(false);
  drawerAppointmentId = signal<string | undefined>(undefined);

  /** Phones get a 3-day view instead of a full week, so the label has to say so. */
  isNarrow = signal<boolean>(this._isNarrowScreen());

  protected readonly legend = [
    { label: 'Predstojeći', ...PALETTE.upcoming },
    { label: 'Završen', ...PALETTE.past },
    { label: 'Otkazan', ...PALETTE.canceled },
    { label: 'Nedolazak', ...PALETTE.noShow },
  ];

  viewOptions = computed(() => [
    { label: this.isNarrow() ? '3 dana' : 'Nedelja', value: 'timeGridWeek' as CalendarView },
    { label: 'Dan', value: 'timeGridDay' as CalendarView },
  ]);

  /** ISO weekdays (1 = Mon … 7 = Sun) the studio works; `undefined` until loaded. */
  private _workingDays: Set<number> | undefined;
  /** Dates (yyyy-m-d, local) overridden to non-working (no intervals). */
  private _closedDates = new Set<string>();

  /** The grid spans the working hours and every loaded appointment (08–20 until settings load). */
  private _workingHours: MinuteRange[] = [{ from: 8 * 60, to: 20 * 60 }];
  private _eventHours: MinuteRange[] = [];
  private _slotWindow: MinuteRange = this._workingHours[0];

  calendarOptions: CalendarOptions = {
    plugins: [timeGridPlugin, interactionPlugin],
    initialView: this._isNarrowScreen() ? 'timeGridThreeDay' : 'timeGridWeek',
    headerToolbar: false,
    height: '100%',
    nowIndicator: true,
    allDaySlot: false,
    ...slotTimes(this._slotWindow),
    slotDuration: '01:00:00',
    slotLabelInterval: '01:00:00',
    slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
    slotLabelContent: (arg) => convertDateToHhMm(arg.date),
    dayHeaderFormat: { weekday: 'short', day: 'numeric' },
    firstDay: 1,
    locale: 'sr-Latn',
    // Opens at the start of the working day (the grid's first row).
    scrollTime: '00:00:00',
    views: {
      timeGridDay: { dayHeaders: false },
      timeGridThreeDay: {
        type: 'timeGrid',
        duration: { days: 3 },
      },
    },
    eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
    eventDisplay: 'block',
    nowIndicatorContent: (arg) => (arg.isAxis ? convertDateToHhMm(arg.date) : undefined),
    dayCellClassNames: (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []),
    dayHeaderClassNames: (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []),
    windowResize: () => this._handleWindowResize(),
    datesSet: (arg) => this._onDatesSet(arg),
    eventClick: (arg) => this._onEventClick(arg),
    events: (info, success, failure) => {
      // Whole days, so appointments outside the hours currently shown are fetched too.
      const range = wholeDays(info.start, info.end);
      this._client.getAppointmentsForCalendar(range.start, range.end).subscribe({
        next: (res) => {
          const events = (res.result ?? []).map((a) => this._toEvent(a));
          success(events);
          this._eventHours = eventRanges(events);
          this._fitHours();
        },
        error: (err) => failure(err),
      });
    },
  };

  titleLabel = computed(() => {
    const start = this.currentStart();
    if (this.view() === 'timeGridDay') {
      return getDateFormatedSr(start);
    }
    return getMonthYearSr(start);
  });

  rangeLabel = computed(() => {
    const start = this.currentStart();
    const end = new Date(this.currentEnd().getTime() - 1);
    if (this.view() === 'timeGridDay') {
      const weekdays = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
      return weekdays[start.getDay()];
    }
    return this._formatRange(start, end);
  });

  badgeMonth = computed(() => {
    return getMonthShortSr(this.currentStart());
  });

  badgeDay = computed(() => {
    return String(this.currentStart().getDate()).padStart(2, '0');
  });

  constructor() {
    this._loadWorkingDays();
    // The now-indicator moves; re-check which hour label it would overlap every minute.
    const timer = setInterval(() => this._syncNowLabelOverlap(), 60_000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  prev() {
    this.fc()?.getApi().prev();
  }

  next() {
    this.fc()?.getApi().next();
  }

  today() {
    this.fc()?.getApi().today();
  }

  changeView(view: CalendarView) {
    this.view.set(view);
    this.fc()?.getApi().changeView(this._actualViewFor(view));
    if (view === 'timeGridDay') {
      const today = new Date();
      this.miniDate.set(today);
      this.fc()?.getApi().gotoDate(today);
    } else {
      this.selectedAppointmentId.set(undefined);
    }
    setTimeout(() => this.fc()?.getApi().updateSize());
  }

  onMiniDateChange(date: Date) {
    if (!date) return;
    this.miniDate.set(date);
    this.fc()?.getApi().gotoDate(date);
  }

  private _isNarrowScreen(): boolean {
    return typeof window !== 'undefined'
      && window.innerWidth < AdminAppointmentsCalendar.MOBILE_BREAKPOINT;
  }

  private _actualViewFor(view: CalendarView): string {
    return view === 'timeGridWeek' && this._isNarrowScreen() ? 'timeGridThreeDay' : view;
  }

  private _handleWindowResize() {
    this.isNarrow.set(this._isNarrowScreen());
    const api = this.fc()?.getApi();
    if (!api) return;
    const targetView = this._actualViewFor(this.view());
    if (api.view.type !== targetView) {
      api.changeView(targetView);
    }
  }

  private _onDatesSet(arg: DatesSetArg) {
    this.currentStart.set(arg.start);
    this.currentEnd.set(arg.end);
    if (arg.view.type === 'timeGridDay') {
      this.miniDate.set(arg.start);
    }
    setTimeout(() => this._syncNowLabelOverlap());
  }

  /** "15. – 17. okt 2026", "29. sep – 5. okt 2026", "29. dec 2026 – 4. jan 2027". */
  private _formatRange(start: Date, end: Date): string {
    if (start.getFullYear() !== end.getFullYear()) {
      return `${getDateFormatedSr(start)} – ${getDateFormatedSr(end)}`;
    }
    if (start.getMonth() !== end.getMonth()) {
      return `${getDayMonthSr(start)} – ${getDateFormatedSr(end)}`;
    }
    return `${start.getDate()}. – ${getDateFormatedSr(end)}`;
  }

  private async _loadWorkingDays(): Promise<void> {
    const [wt, overrides] = await Promise.all([
      firstValueFrom(this._workingTime.getWorkingTime()),
      firstValueFrom(this._workingTime.getDateOverrides()),
    ]);
    // All day state first: fitting the hours below already re-renders the grid.
    this._workingDays = new Set(wt.result?.workingDays ?? []);
    this._closedDates = new Set(
      (overrides.result?.dateOverrides ?? [])
        .filter((o) => o.date && !(o.intervals?.length))
        .map((o) => this._dateKey(o.date!)),
    );
    const hours = workingHoursRanges(wt.result, overrides.result?.dateOverrides);
    if (hours.length > 0) {
      this._workingHours = hours;
      this._fitHours();
    }
    // Class-name callbacks only run on render; FullCalendar ignores re-setting the same function,
    // so pass new ones to force the day cells and headers to re-render with the loaded state.
    const api = this.fc()?.getApi();
    api?.setOption('dayCellClassNames', (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []));
    api?.setOption('dayHeaderClassNames', (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []));
  }

  private _fitHours(): void {
    this._slotWindow = fitSlotTimes(this.fc()?.getApi(), this._slotWindow, [
      ...this._workingHours,
      ...this._eventHours,
    ]);
    setTimeout(() => this._syncNowLabelOverlap());
  }

  /** Mini datepicker cell → same non-working check as the grid (month is 0-based). */
  isNonWorkingDate(cell: { day: number; month: number; year: number }): boolean {
    return this._isNonWorkingDay(new Date(cell.year, cell.month, cell.day));
  }

  private _isNonWorkingDay(date: Date): boolean {
    if (this._closedDates.has(this._dateKey(date))) return true;
    if (!this._workingDays || this._workingDays.size === 0) return false;
    const isoDay = date.getDay() === 0 ? 7 : date.getDay();
    return !this._workingDays.has(isoDay);
  }

  private _dateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  /**
   * Hides the hour label the current-time label would overlap (e.g. "11:48" sitting on "12:00").
   * A slot is 3rem (48px) per hour and a label is 18px tall → labels within ~23 min collide.
   */
  private _syncNowLabelOverlap(): void {
    const now = new Date();
    const todayInView = now >= this.currentStart() && now < this.currentEnd();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const labels = this._host.nativeElement.querySelectorAll<HTMLElement>('.fc-timegrid-slot-label[data-time]');
    labels.forEach((label) => {
      const [h, m] = (label.dataset['time'] ?? '').split(':').map(Number);
      const near = todayInView && Math.abs(h * 60 + m - nowMinutes) < 23;
      label.classList.toggle('slot-label--near-now', near);
    });
  }

  private _toEvent(a: AppointmentForCalendarResponse): EventInput {
    const status = a.status?.value;
    const isPast = a.to ? a.to.getTime() < Date.now() : false;
    const palette = this._palette(status, isPast);
    return {
      id: a.id,
      title: a.clientName ?? '',
      start: a.from,
      end: a.to,
      backgroundColor: palette.bg,
      borderColor: palette.border,
      textColor: palette.text,
      extendedProps: {
        status,
        clientName: a.clientName,
        from: a.from,
        to: a.to,
      },
    };
  }

  private _palette(status: AppointmentStatus | undefined, isPast: boolean): EventPalette {
    if (status === AppointmentStatus.CANCELED) return PALETTE.canceled;
    if (status === AppointmentStatus.NO_SHOW) return PALETTE.noShow;
    if (isPast) return PALETTE.past;
    return PALETTE.upcoming;
  }

  private _onEventClick(arg: EventClickArg) {
    const id = arg.event.id;
    if (!id) return;
    if (this.view() === 'timeGridDay') {
      this.selectedAppointmentId.set(id);
    } else {
      this.drawerAppointmentId.set(id);
      this.showDrawer.set(true);
    }
  }

  onActionFromDrawer() {
    this._refresh();
  }

  onActionFromInline() {
    this._refresh();
  }

  private _refresh() {
    this.fc()?.getApi().refetchEvents();
  }

  formatTime(date: Date | null): string {
    if (!date) return '';
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  /** "16:30 - 17:30"; compact "16:30–17:30" in the narrow 3-day columns, which can wrap after the dash. */
  formatTimeRange(start: Date | null, end: Date | null): string {
    const sep = this.isNarrow() ? '–' : ' - ';
    return `${this.formatTime(start)}${sep}${this.formatTime(end)}`;
  }

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
}
